import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useMasterData } from "../../../hooks/useMasterData";
import {
  grievanceApi,
  GrievanceFilters,
  GrievanceListItem,
  GrievancePriority,
  GrievanceStatus,
  GrievanceType,
} from "./grievanceApi";
import { studentsApi } from "../students/api";

interface FilterState extends GrievanceFilters {
  page: number;
  limit: number;
}

interface StudentOption {
  id: string;
  name: string;
  admissionNumber: string;
}

const statusColor: Record<GrievanceStatus, "default" | "primary" | "warning" | "success" | "error"> = {
  OPEN: "error",
  IN_PROGRESS: "warning",
  RESOLVED: "success",
  CLOSED: "default",
};

const priorityColor: Record<GrievancePriority, "default" | "primary" | "warning" | "success" | "error"> = {
  LOW: "default",
  MEDIUM: "primary",
  HIGH: "error",
};

const GrievanceList: React.FC = () => {
  const navigate = useNavigate();
  const { masters, fetchMasters, fetchSections } = useMasterData();

  const [filters, setFilters] = useState<FilterState>({
    academicSessionId: "",
    classId: "",
    sectionId: "",
    status: undefined,
    priority: undefined,
    studentId: "",
    type: undefined,
    fromDate: "",
    toDate: "",
    page: 0,
    limit: 10,
  });

  const [students, setStudents] = useState<StudentOption[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<GrievanceListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get("/system-settings/academic-sessions");
      setSessions(res.data || []);
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    fetchMasters();
    loadSessions();
  }, [fetchMasters, loadSessions]);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then((secs) => setSections(secs || []));
    } else {
      setSections([]);
    }
  }, [filters.classId, fetchSections]);

  const loadStudents = useCallback(async () => {
    if (!filters.classId || !filters.sectionId) {
      setStudents([]);
      return;
    }
    try {
      const res = await studentsApi.getAll({
        page: 1,
        limit: 100,
        classId: filters.classId,
        sectionId: filters.sectionId,
      });
      const list = res.data || [];
      setStudents(
        list.map((s: any) => ({
          id: s.id,
          name: `${s.firstName} ${s.lastName}`,
          admissionNumber: s.admissionNumber,
        })),
      );
    } catch {
      setStudents([]);
    }
  }, [filters.classId, filters.sectionId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const fetchGrievances = useCallback(async () => {
    setLoading(true);
    try {
      const response = await grievanceApi.getAll(
        {
          academicSessionId: filters.academicSessionId || undefined,
          classId: filters.classId || undefined,
          sectionId: filters.sectionId || undefined,
          status: filters.status || undefined,
          priority: filters.priority || undefined,
          studentId: filters.studentId || undefined,
          type: filters.type || undefined,
          fromDate: filters.fromDate || undefined,
          toDate: filters.toDate || undefined,
        },
        filters.page + 1,
        filters.limit,
      );
      setItems(response.data);
      setTotal(response.meta.total);
      setStats(response.meta.stats);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchGrievances();
  }, [fetchGrievances]);

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 0,
    }));
  };

  const handleChangePage = (_: any, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this grievance?")) {
      return;
    }
    try {
      await grievanceApi.remove(id);
      fetchGrievances();
    } catch {
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={500}>
          Student Grievances / Suggestions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/scholar-register/grievance/create")}
        >
          Create Grievance
        </Button>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Total
              </Typography>
              <Typography variant="h6">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Open
              </Typography>
              <Typography variant="h6">{stats.open}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                In Progress
              </Typography>
              <Typography variant="h6">{stats.inProgress}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Resolved
              </Typography>
              <Typography variant="h6">{stats.resolved}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Closed
              </Typography>
              <Typography variant="h6">{stats.closed}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Session"
              size="small"
              value={filters.academicSessionId || ""}
              onChange={(e) => handleFilterChange("academicSessionId", e.target.value || "")}
            >
              <MenuItem value="">All</MenuItem>
              {sessions.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Class"
              size="small"
              value={filters.classId || ""}
              onChange={(e) => handleFilterChange("classId", e.target.value || "")}
            >
              <MenuItem value="">All</MenuItem>
              {masters.classes.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Section"
              size="small"
              value={filters.sectionId || ""}
              onChange={(e) => handleFilterChange("sectionId", e.target.value || "")}
            >
              <MenuItem value="">All</MenuItem>
              {sections.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Student"
              size="small"
              value={filters.studentId || ""}
              onChange={(e) => handleFilterChange("studentId", e.target.value || "")}
            >
              <MenuItem value="">All</MenuItem>
              {students.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.admissionNumber} - {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              size="small"
              value={filters.status || ""}
              onChange={(e) =>
                handleFilterChange("status", (e.target.value || "") as GrievanceStatus | "")
              }
            >
              <MenuItem value="">All</MenuItem>
              {["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"].map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace("_", " ")}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Priority"
              size="small"
              value={filters.priority || ""}
              onChange={(e) =>
                handleFilterChange(
                  "priority",
                  (e.target.value || "") as GrievancePriority | "",
                )
              }
            >
              <MenuItem value="">All</MenuItem>
              {["LOW", "MEDIUM", "HIGH"].map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Type"
              size="small"
              value={filters.type || ""}
              onChange={(e) =>
                handleFilterChange("type", (e.target.value || "") as GrievanceType | "")
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="GRIEVANCE">Grievance</MenuItem>
              <MenuItem value="SUGGESTION">Suggestion</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="From Date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.fromDate || ""}
              onChange={(e) => handleFilterChange("fromDate", e.target.value || "")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="To Date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.toDate || ""}
              onChange={(e) => handleFilterChange("toDate", e.target.value || "")}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticket No</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.ticketNumber}</TableCell>
                  <TableCell>
                    {row.student
                      ? `${row.student.firstName} ${row.student.lastName}`
                      : "-"}
                  </TableCell>
                  <TableCell>{row.class ? row.class.name : "-"}</TableCell>
                  <TableCell>{row.section ? row.section.name : "-"}</TableCell>
                  <TableCell>{row.type === "GRIEVANCE" ? "Grievance" : "Suggestion"}</TableCell>
                  <TableCell>{row.category}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.priority}
                      color={priorityColor[row.priority]}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status.replace("_", " ")}
                      color={statusColor[row.status]}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(row.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/scholar-register/grievance/${row.id}`)
                      }
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/scholar-register/grievance/${row.id}/edit`)
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(row.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Box textAlign="center" py={3}>
                      <Typography color="textSecondary">
                        No grievances found.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={filters.page}
          onPageChange={handleChangePage}
          rowsPerPage={filters.limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Container>
  );
};

export default GrievanceList;
